/**
 * Hands off dashboard session + SDK keys to the Setup app.
 *
 * **Default:** GET + **hash** (`#intrackSdkSetup=…`): fragment is not sent to the server; the Setup
 * SPA reads it after `index.html` loads. Avoids nginx **405** on `POST /` for typical static SPA hosts.
 *
 * **Optional HTML form POST:** field {@link HANDOFF_FIELD_NAME}. Enable only if nginx accepts POST
 * on the setup URL: set `SETUP_HANDOFF_USE_HASH: false` in env.json.
 *
 * **Local Vite (localhost:5173):** POST / often 404; hash is used unless POST middleware exists.
 *
 * Optional env.json (window.__ENV_CONFIG__):
 * - SETUP_APP_LOCAL_ORIGIN (default http://localhost:5173)
 * - SETUP_APP_ORIGIN (default https://setup.intrack.ir)
 * - SETUP_POST_PATH (default "/"; legacy: SETUP_HANDOFF_PATH)
 * - SETUP_HANDOFF_USE_HASH (boolean): `false` = use POST handoff; omitted/true = hash (default).
 */

export const HANDOFF_FIELD_NAME = 'dashboardPayload';

/** Setup SPA reads this key from `location.hash` after `#`. */
export const SETUP_HASH_KEY = 'intrackSdkSetup';

// const DEFAULT_LOCAL_ORIGIN = 'http://localhost:5173';
const DEFAULT_PROD_ORIGIN = 'https://setup.intrack.ir';
/** POST target path under the setup origin (default root). */
const DEFAULT_POST_PATH = '/';

function getEnv() {
  return typeof window !== 'undefined' ? window.__ENV_CONFIG__ || {} : {};
}

// export function isLocalDashboardHostname() {
//   if (typeof window === 'undefined') {
//     return false;
//   }
//   const h = window.location.hostname;
//   return h === 'localhost' || h === '127.0.0.1';
// }

/** True when the configured setup app origin is localhost (Vite cannot POST / without middleware). */
// export function isSetupOriginLocalDev() {
//   try {
//     const u = new URL(getSetupAppOrigin());
//     return u.hostname === 'localhost' || u.hostname === '127.0.0.1';
//   } catch {
//     return false;
//   }
// }

/**
 * Hash handoff is the default (nginx often returns 405 for POST to `/` on static Setup hosting).
 * Set `SETUP_HANDOFF_USE_HASH: false` in env.json only when POST to the setup app is allowed.
 */
export function shouldUseHashHandoff() {
  const env = getEnv();
  if (
    env.SETUP_HANDOFF_USE_HASH === false ||
    env.setupHandoffUseHash === false
  ) {
    return false;
  }
  return true;
}

/** Base URL of the Setup SPA (no trailing slash). */
export function getSetupAppOrigin() {
  const env = getEnv();
  // if (isLocalDashboardHostname()) {
  //   return (env.SETUP_APP_LOCAL_ORIGIN || env.setupAppLocalOrigin).replace(
  //     /\/$/,
  //     ''
  //   );
  // }
  return (
    env.SETUP_APP_ORIGIN ||
    env.setupAppOrigin ||
    DEFAULT_PROD_ORIGIN
  ).replace(/\/$/, '');
}

/** Full URL that receives the POST (setup app root "/" by default). */
export function getSetupPostUrl() {
  const env = getEnv();
  const raw =
    env.SETUP_POST_PATH ||
    env.setupPostPath ||
    env.SETUP_HANDOFF_PATH ||
    env.setupHandoffPath ||
    DEFAULT_POST_PATH;
  const path = raw === '' ? DEFAULT_POST_PATH : raw;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${getSetupAppOrigin()}${normalizedPath}`;
}

function jsonToBase64UrlPayload(json) {
  const utf8 = unescape(encodeURIComponent(json));
  return btoa(utf8);
}

/** Opens setup app in a new tab with payload in URL hash (default handoff). */
function openSetupWithHashPayload(payload) {
  const postUrl = getSetupPostUrl();
  const u = new URL(postUrl);
  const b64 = jsonToBase64UrlPayload(JSON.stringify(payload));
  u.hash = `${SETUP_HASH_KEY}=${encodeURIComponent(b64)}`;
  window.open(u.toString(), '_blank', 'noopener,noreferrer');
}

/**
 * Sends handoff to the setup app (opens in a new tab): hash GET by default, or POST if opted in.
 * @param {Record<string, unknown>} payload
 */
export function submitSdkSetupHandoff(payload) {
  if (process.env.NODE_ENV === 'development') {
    // Pretty-print so nested `sdk` is visible (raw object logs often show `{…}` / "No properties").
    console.info(
      '[sdk-setup-handoff] outgoing payload (JSON)\n%s',
      JSON.stringify(payload, null, 2)
    );
    console.info(
      '[sdk-setup-handoff] mode:',
      shouldUseHashHandoff()
        ? 'hash on GET (see full URL in new tab)'
        : 'POST field dashboardPayload'
    );
  }

  if (shouldUseHashHandoff()) {
    openSetupWithHashPayload(payload);
    return;
  }

  const url = getSetupPostUrl();
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;
  form.target = '_blank';
  form.rel = 'noopener noreferrer';
  form.enctype = 'application/x-www-form-urlencoded';

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = HANDOFF_FIELD_NAME;
  input.value = JSON.stringify(payload);
  form.appendChild(input);

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
}
